import { useEffect, useMemo, useRef, useState } from "react";
import { get, useFormContext } from "react-hook-form";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "../../../components/buttons/Button";
import { DropdownDynamicList } from "../../../components/dropdowns/DropDownDynamicList";
import { InputRFH } from "../../../components/inputs/InputRFH";
import {
    API,
    API_BASEURL,
    FORM_MSG,
    PERMISSIONS,
    ROLES,
    SWR_CONFIG,
    buildUrl,
} from "../../../config";
import {
    calculateProgramPrice,
    getFieldsByProgram,
    getProgramMaxPrice,
    isClubFico,
} from "../../../containers/contact-shared/programs/helpers";
import {
    CreditReportService,
    ExtractedCreditReportData,
} from "../../../containers/contact-shared/credit-report/api/credit-report.service";
import useGetSWR from "../../../hooks/useGetSWR";
import { CloseIcon, EyeIconFilled } from "../../../icons";
import { CrPricingRes, IDocumentRes } from "../../../types";
import { CreateProgramPopup } from "../../../types/ClientProgram";
import { usePermissions } from "../../../hooks";
import { CreateProgramField } from "../../../containers/contact-shared/programs/components/CreateProgramField";
import { DropDownUsers } from "../../../components/dropdowns";
import { ErrorText } from "../../../components/forms/ErrorText";
import Loader from "react-loader-spinner";
import { fetcherGetDownload, getDocumentName } from "../../../utils";
import { getToken } from "../../../utils/auth";

/**
 * If validating false --> add new program popup comming from popup tab
 * If validating true // addProgram false --> validating program by Juan
 * If validating and addProgram true --> validate price by agent and add program
 */
interface ValidateProgramPopupParams {
    person_id: string;
    clickFunctionCancelButton?: () => void;
    program: string;
    backFunction?: () => void;
    cancelFunction?: () => void;
    onSubmit?: () => void;
    onCloseFunction?: () => void;
    isUpdating: boolean;
    validating?: boolean;
    addProgram?: boolean;
    showInfo?: boolean;
    quote?: boolean;
    priceError?: string;
    setPriceError?: (val: string) => void;
    lead?: boolean;
    onlySaveQuote?: boolean;
    onEditQuote?: () => void;
    onEditProgram?: () => void;
}

export const ValidateProgramPopup = ({
    quote,
    clickFunctionCancelButton,
    person_id,
    program,
    addProgram,
    backFunction,
    onSubmit,
    onCloseFunction,
    cancelFunction,
    isUpdating,
    validating,
    showInfo,
    setPriceError,
    priceError,
    lead = false,
    onlySaveQuote,
    onEditQuote,
    onEditProgram,
}: ValidateProgramPopupParams) => {
    const { checkPermission } = usePermissions();
    const isFinalValidation = useMemo(() => addProgram && validating, [addProgram, validating]);
    const isValidating = useMemo(
        () => !showInfo && !quote && validating,
        [quote, showInfo, validating]
    );

    const [fields, setFields] = useState<any>({});
    const [isEditingProgram, setIsEditingProgram] = useState(false);
    const [initialProgramValues, setInitialProgramValues] = useState<CreateProgramPopup | null>(null);
    const canEditProgram = !!showInfo && checkPermission(PERMISSIONS.CLIENT_UPDATE_PROGRAM_INFO);
    const urlCrPricing = buildUrl(API.CR_PRICING, {}, API_BASEURL, {});
    const { data: crPricingData } = useGetSWR<CrPricingRes>(urlCrPricing, SWR_CONFIG.FORMS);

    // CLRDC-2234: AI-assisted credit-report reading. Only enabled for
    // CLUB programs while we are creating a new program (not when
    // validating an existing one or showing its info).
    const showCreditReportUpload =
        isClubFico(program) && !showInfo && !validating && !quote;
    // CLRDC-2234: pulled out separately so we can render it at the very
    // top of the popup instead of its normal position in `fields.fields`.
    const creditReportField = useMemo(
        () => fields?.fields?.find((f: any) => f.accesKey === "credit_report") ?? null,
        [fields]
    );
    const auth0 = useAuth0();
    const creditReportServiceRef = useRef<CreditReportService | null>(null);
    if (!creditReportServiceRef.current) {
        creditReportServiceRef.current = new CreditReportService();
    }
    const [creditReportViewerUrl, setCreditReportViewerUrl] = useState<string | null>(null);
    // CLRDC-2234: lets the agent close the side viewer without losing
    // the already-downloaded PDF, so it can be reopened instantly via
    // the eye icon without re-downloading/re-analyzing anything.
    const [isCreditReportViewerVisible, setIsCreditReportViewerVisible] = useState(false);
    const [creditReportDocumentName, setCreditReportDocumentName] = useState<string | null>(null);
    const [isAnalyzingCreditReport, setIsAnalyzingCreditReport] = useState(false);
    const [creditReportError, setCreditReportError] = useState<string | null>(null);
    const [creditReportApplied, setCreditReportApplied] = useState(false);
    // CLRDC-2234: the downloaded PDF, kept in memory so the "Analyze
    // with AI" button can send it to the AI endpoint without
    // re-downloading it from S3. The AI call is opt-in now — it is
    // no longer fired automatically when a document is selected.
    const [creditReportFile, setCreditReportFile] = useState<File | null>(null);
    // Track the last `credit_report` id we already downloaded/prepared
    // so rapid re-renders (or `documentRes` revalidation right after an
    // upload) don't re-trigger the download.
    const lastAnalyzedCreditReportRef = useRef<string | null>(null);

    // Fetch the same document list that `SelectCreditReport` reads, so
    // we can resolve the selected `credit_report` id into its S3 key
    // and download the PDF for the AI service without touching the
    // shared component's interface.
    const documentsUrl = buildUrl(
        lead ? API.LEAD_DOCUMENT_ALL : API.CLIENT_DOCUMENT_ALL,
        {},
        API_BASEURL,
        { id: person_id }
    );
    const { data: documentRes } = useGetSWR<IDocumentRes>(
        showCreditReportUpload ? documentsUrl : null,
        SWR_CONFIG.FORMS
    );

    // Revoke the object URL when the file changes/unmounts so we don't leak memory.
    useEffect(() => {
        return () => {
            if (creditReportViewerUrl) {
                URL.revokeObjectURL(creditReportViewerUrl);
            }
        };
    }, [creditReportViewerUrl]);

    useEffect(() => {
        setFields(getFieldsByProgram(program));
    }, [program]);

    const {
        register,
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
        reset,
        formState: { errors },
    } = useFormContext<CreateProgramPopup>();

    useEffect(() => {
        if (!showInfo) {
            setIsEditingProgram(false);
            setInitialProgramValues(null);
        }
    }, [showInfo]);

    const options = watch();
    useEffect(() => {
        if (fields.fields) {
            for (const f of fields.fields) {
                if (showInfo && !isEditingProgram) continue;
                if (f.onlyFinal && !isFinalValidation) continue;
                if ((f as any).automated) {
                    register(f.accesKey as any, { required: f.required });
                    setValue(
                        f.accesKey as any,
                        (f as any).automated({ ...options, isFinalValidation, isValidating })
                    );
                } else if (f.type === "Bureaus" && f.required) {
                    register("ex", {
                        value: false,
                        validate: (val) => {
                            if (!val && !getValues().eq && !getValues().tu)
                                return "* Please select at least one bureau.";
                            else return undefined;
                        },
                    });
                    register("eq", { value: false });
                    register("tu", { value: false });
                    // setError('ex', { type: 'validate', message: '* Please select at least one bureau.' });
                } else if (f.type === "CreditReport") {
                    register("credit_report", { required: true });
                } else if (f.accesKey === "club") {
                    register("club", { required: true });
                } else if (
                    f.type !== "Accounts" &&
                    f.type !== "Collections" &&
                    f.accesKey !== "special_pricing" &&
                    f.accesKey !== "mix_social_itin" &&
                    f.type !== "Payments"
                ) {
                    register(f.accesKey, {
                        required: !f.includeInType
                            ? true
                            : f.includeInType.includes(options.club_type),
                    });
                } else if (
                    f.type === "Accounts" &&
                    isClubFico(program) &&
                    options.club_type === "VIP"
                ) {
                    register("accounts_negative", { required: true });
                    register("accounts_positive", {
                        required: true,
                        validate: (val) => {
                            if (Number(val) + Number(getValues().accounts_negative) > 0)
                                return "Accounts must be 0.";
                            return undefined;
                        },
                    });
                } else if (f.type === "Accounts" && program === "VIP Credit") {
                    register("accounts_negative", {
                        validate: (val) => {
                            if (Number(val) === 0) return "At least one account must be negative.";
                            return undefined;
                        },
                    });
                } else if (f.type === "Accounts") {
                    register("accounts_negative", { required: true, validate: () => undefined });
                    register("accounts_positive", { required: true, validate: () => undefined });
                    clearErrors("accounts_positive");
                    clearErrors("accounts_negative");
                }
            }
            register("seller_id", { required: true });
            register("agent_id", { required: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [fields, validating, addProgram, options.club_type, showInfo, isEditingProgram]);

    useEffect(() => {
        if (!crPricingData) return;
        const price = parseFloat(getValues().price as any);
        let maxPrice = null;
        if (!options.special_pricing)
            maxPrice = getProgramMaxPrice(program, crPricingData!.cr_pricing || 50, {
                ...options,
            } as any);
        if (price) {
            if ((getValues().cost || 0) > price)
                return setPriceError && setPriceError("* Price cannot be lower than cost.");
            else if (price < 0)
                return setPriceError && setPriceError("* Price cannot be negative.");
            else if (maxPrice !== null && price > maxPrice)
                return (
                    setPriceError && setPriceError(`* Price cannot be higher than $${maxPrice}.`)
                );
            else if (price >= (getValues().cost || 0) && price > 0)
                return setPriceError && setPriceError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.price, options.cost, options.special_pricing, crPricingData]);

    useEffect(() => {
        if (fields && crPricingData) {
            if ((showInfo && !isEditingProgram) || isEditingProgram || (validating && options.special_pricing)) return;
            const { cost } = calculateProgramPrice(
                program,
                crPricingData.cr_pricing,
                options.num_payments,
                {
                    ...options,
                } as any
            );
            setValue("cost", cost as number);
            const price = fields.fields.findIndex((f: any) => f.accesKey === "price");
            if (price > -1 && fields.fields[price].automated) {
                setValue(
                    "price",
                    fields.fields[price].automated({
                        ...options,
                        isFinalValidation,
                        isValidating,
                    }) || "0"
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        options.name,
        options.accounts_positive,
        options.accounts_negative,
        options.tradelines,
        options.bk_type,
        options.cost,
        options.price,
        options.inquiries,
        options.simulations,
        options.records,
        options.ex,
        options.eq,
        options.tu,
        options.state,
        options.club,
        options.num_payments,
        options.amount_approved,
        options.property_value,
        options.club_type,
        options.type,
        options.state,
        options.zip,
        options.account_1,
        options.account_2,
        options.account_3,
        options.creditor_1,
        options.creditor_2,
        options.creditor_3,
        options.collection_amount_1,
        options.collection_amount_2,
        options.collection_amount_3,
        options.tradelines_type,
        options.repositions,
        options.num_disputes,
        options.num_copies,
        crPricingData,
    ]);

    useEffect(() => {
        if (program !== "Club Fico Guaranteed") return;
        const scores = [options.eq, options.ex, options.tu].filter((s) => s !== undefined);
        const scores0 = scores.filter((s) => String(s) === "0" || s === 0);
        if (scores.length === 3 && scores0.length === 2) {
            setValue("club", "CLUB 620");
        } else if (options.mix_social_itin) {
            setValue("club", "CLUB 620");
        }
    }, [
        program,
        options.eq,
        options.tu,
        options.ex,
        options.mix_social_itin,
        setValue,
    ]);

    useEffect(() => {
        if (fields.fields) {
            const tradelines = fields.fields.findIndex((f: any) => f.accesKey === "tradelines");
            const price = fields.fields.findIndex((f: any) => f.accesKey === "price");
            if (tradelines > 0 && !isFinalValidation) {
                if (fields.fields[tradelines].automated)
                    setValue(
                        "tradelines",
                        fields.fields[tradelines].automated({
                            ...options,
                            isFinalValidation,
                            isValidating,
                        }) || "0"
                    );
            }
            if (price > 0 && program === "Tradelines Funding") {
                if (fields.fields[price].automated)
                    setValue(
                        "price",
                        fields.fields[price].automated({
                            ...options,
                            isFinalValidation,
                            isValidating,
                        }) || "0"
                    );
            }
            if (program === "ID Theft") {
                if (Number(options.accounts_negative) + Number(options.accounts_positive) > 10) {
                    setError("accounts_positive", {
                        type: "validate",
                        message: "Max 10 accounts.",
                    });
                } else {
                    clearErrors("accounts_positive");
                }
            }
            if (isClubFico(program) && options.club_type === "VIP") {
                if (Number(options.accounts_negative) + Number(options.accounts_positive) !== 0) {
                    setError("accounts_positive", {
                        type: "validate",
                        message: "Accounts must be 0.",
                    });
                } else {
                    clearErrors("accounts_positive");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.accounts_negative, options.accounts_positive]);

    useEffect(() => {
        if (fields.fields) {
            const price = fields.fields.findIndex((f: any) => f.accesKey === "price");
            if (price > 0 && fields.fields[price].automated) {
                setValue(
                    "price",
                    fields.fields[price].automated({
                        ...options,
                        isFinalValidation,
                        isValidating,
                    }) || "0"
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.amount_approved]);

    useEffect(() => {
        if (fields.fields) {
            const midFico = fields.fields.findIndex((f: any) => f.accesKey === "mid_fico");
            if (midFico > -1 && fields.fields[midFico].automated) {
                setValue(
                    "mid_fico",
                    fields.fields[midFico].automated({
                        ...options,
                        isFinalValidation,
                        isValidating,
                    }) || "0"
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.ex, options.eq, options.tu]);

    useEffect(() => {
        if (fields.fields) {
            const estimated_deposit = fields.fields.findIndex(
                (f: any) => f.accesKey === "estimated_deposit"
            );
            if (estimated_deposit > 0 && fields.fields[estimated_deposit].automated) {
                setValue(
                    "estimated_deposit",
                    fields.fields[estimated_deposit].automated({ ...options }) || "0"
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.funding_type]);

    const handleClosePopup = () => {
        if (clickFunctionCancelButton) clickFunctionCancelButton();
        onCloseFunction && onCloseFunction();
    };

    const goBack = (e: any) => {
        e.preventDefault();
        backFunction && backFunction();
    };

    const getTitle = () => {
        if (quote) return "Quote";
        if (showInfo) return "Info";
        return "Validation";
    };

    const getErrorType = (key: string) => get(errors, key)?.type;

    const handleStartEditing = () => {
        setInitialProgramValues(getValues());
        setIsEditingProgram(true);
    };

    // CLRDC-2234: pipe the AI response into react-hook-form. We only
    // setValue non-null values so the form's existing rules (e.g.
    // `accounts_negative` validators) stay in charge of clearing.
    const applyCreditReportData = (data: ExtractedCreditReportData) => {
        const fieldsToSet: Array<[keyof CreateProgramPopup, unknown]> = [
            ["eq", data.eq],
            ["tu", data.tu],
            ["ex", data.ex],
            ["accounts_positive", data.accounts_positive],
            ["accounts_negative", data.accounts_negative],
            ["num_collections", data.num_collections],
            ["num_disputes", data.num_disputes],
            ["num_inquiries", data.num_inquiries],
            ["num_public_records", data.num_public_records],
            ["num_open_accounts", data.num_open_accounts],
            ["repositions", data.repositions],
            ["mix_social_itin", data.mix_social_itin],
            ["bk_type", data.bk_type],
        ];
        for (const [key, value] of fieldsToSet) {
            if (value !== null && value !== undefined) {
                setValue(key as any, value as any);
            }
        }
    };

    // CLRDC-2234: just hide the side viewer — the doc stays picked, the
    // AI fields stay applied, and the downloaded blob is kept around
    // so `handleReopenCreditReportViewer` can bring it back instantly.
    const handleCloseCreditReportViewer = () => {
        setIsCreditReportViewerVisible(false);
    };

    // CLRDC-2234: re-show the side viewer after the agent closed it,
    // without re-downloading or re-analyzing the PDF.
    const handleReopenCreditReportViewer = () => {
        setIsCreditReportViewerVisible(true);
    };

    // CLRDC-2234: when the user picks (or uploads) a credit report via
    // the existing `SelectCreditReport` field, the form value
    // `credit_report` flips to that document's id. We resolve it back
    // to the underlying S3 object, download its bytes and render the
    // PDF in the side viewer.
    //
    // The AI extraction is opt-in: it no longer fires automatically
    // here. It only runs when the agent clicks "Analyze with AI"
    // (see `handleAnalyzeCreditReport` below), so they can decide
    // whether to use it at all.
    //
    // Re-runs are guarded by `lastAnalyzedCreditReportRef` so that
    // `documentRes` revalidation right after an upload (or any other
    // unrelated re-render) doesn't re-download the same document.
    useEffect(() => {
        if (!showCreditReportUpload) return;

        const selectedId = (options.credit_report as string | undefined) ?? "";

        if (!selectedId) {
            // Doc deselected — wipe local analysis state. The cleanup
            // effect on `creditReportViewerUrl` will revoke the blob URL.
            setCreditReportError(null);
            setCreditReportApplied(false);
            setCreditReportDocumentName(null);
            setCreditReportViewerUrl(null);
            setIsCreditReportViewerVisible(false);
            setCreditReportFile(null);
            lastAnalyzedCreditReportRef.current = null;
            return;
        }

        if (lastAnalyzedCreditReportRef.current === selectedId) return;
        if (!documentRes) return;

        const docKey = lead ? "lead_document" : "client_document";
        const document = documentRes[docKey]?.find((d) => d.id === selectedId);
        if (!document) {
            // SWR hasn't refreshed yet with the freshly-uploaded id.
            // We'll be re-evaluated on the next `documentRes` tick.
            return;
        }

        // Mark first so concurrent re-renders don't fire twice.
        lastAnalyzedCreditReportRef.current = selectedId;

        let cancelled = false;
        const loadSelected = async () => {
            setCreditReportError(null);
            setCreditReportApplied(false);
            setCreditReportDocumentName(getDocumentName(document.name));

            try {
                const token = await getToken(auth0);

                // Download the binary from S3 through the existing
                // `CLIENT_DOCUMENT_VIEW`/`LEAD_DOCUMENT_VIEW` route, the
                // same pattern used by `useViewDocument`.
                const viewUrl = buildUrl(
                    lead ? API.LEAD_DOCUMENT_VIEW : API.CLIENT_DOCUMENT_VIEW,
                    {},
                    API_BASEURL,
                    { key: document.url }
                );
                const [result, downloadErr] = await fetcherGetDownload(
                    viewUrl,
                    token
                );
                if (cancelled) return;
                if (downloadErr || !result) {
                    throw new Error(
                        "Failed to download the credit report from storage."
                    );
                }

                const arrayBuffer = await result.arrayBuffer();
                const blob = new Blob([arrayBuffer], {
                    type: "application/pdf",
                });
                const blobUrl = URL.createObjectURL(blob);
                if (cancelled) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }
                // Swap in the new viewer URL. The cleanup effect on
                // `creditReportViewerUrl` will revoke the previous one.
                setCreditReportViewerUrl(blobUrl);
                setIsCreditReportViewerVisible(true);

                // Keep the `File` around so the "Analyze with AI"
                // button can send it without re-downloading.
                const file = new File(
                    [arrayBuffer],
                    document.name || "credit-report.pdf",
                    { type: "application/pdf" }
                );
                setCreditReportFile(file);
            } catch (err: any) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error(
                    "[ValidateProgramPopup] credit-report load failed:",
                    err
                );
                setCreditReportError(
                    err?.message ??
                        "Could not load the credit report. Please try again."
                );
                setCreditReportViewerUrl(null);
                setIsCreditReportViewerVisible(false);
                setCreditReportFile(null);
            }
        };

        loadSelected();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        options.credit_report,
        documentRes,
        showCreditReportUpload,
        lead,
        person_id,
    ]);

    // CLRDC-2234: fired only when the agent explicitly clicks
    // "Analyze with AI" — the analysis is opt-in, not automatic.
    const handleAnalyzeCreditReport = async () => {
        if (!creditReportFile || isAnalyzingCreditReport) return;

        setCreditReportError(null);
        setCreditReportApplied(false);
        setIsAnalyzingCreditReport(true);

        try {
            const token = await getToken(auth0);
            const data: ExtractedCreditReportData =
                await creditReportServiceRef.current!.analyzePdf(
                    token,
                    creditReportFile
                );

            applyCreditReportData(data);
            setCreditReportApplied(true);
        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error(
                "[ValidateProgramPopup] credit-report AI failed:",
                err
            );
            setCreditReportError(
                err?.message ??
                    "Could not analyze the credit report. Please try again."
            );
        } finally {
            setIsAnalyzingCreditReport(false);
        }
    };

    const handleCancelEditing = () => {
        if (initialProgramValues) {
            reset(initialProgramValues);
        }
        setIsEditingProgram(false);
    };

    return (
        <div
            className={`${
                validating && "bg-gray-200/70"
            } fixed z-[99] inset-0 flex items-center justify-center bground`}
            onClick={function (e) {
                const target = e.target as HTMLDivElement;
                if (target.classList.contains("bground")) {
                    handleClosePopup();
                }
            }}
        >
            <div
                className={`flex items-stretch justify-center gap-4 w-full max-w-[95vw] ${
                    creditReportViewerUrl && isCreditReportViewerVisible ? "p-4" : ""
                }`}
            >
            <div
                className={`max-h-[95vh] flex flex-col gap-6 items-center bg-color8 custom-scroll ${
                    validating && !addProgram
                        ? fields.validateWidth || fields.popupWidth
                        : fields?.popupWidth
                } p-9 rounded-[2rem] shadow-md relative ${isClubFico(program) ? "overflow-y-auto" : "overflow-visible"}`}
            >
                <div className="w-full">
                    <CloseIcon
                        className="fill-current text-color6 absolute right-2 top-2 mr-1 mt-1 cursor-pointer"
                        onClick={() => handleClosePopup()}
                    />
                    <div className="w-full">
                        <p className="font-inter font-bold text-xl">
                            {program} {getTitle()}
                        </p>
                    </div>
                    {/* CLRDC-2234: the credit-report upload must sit at
                        the very top of the popup, above every other
                        field, so the agent uploads/selects it first and
                        the rest of the form can be auto-filled by the
                        AI before they touch anything else. We pull the
                        "CreditReport" field out of its normal spot in
                        `fields.fields` and render it here instead; the
                        loop below explicitly skips it so it isn't
                        rendered twice. */}
                    {showCreditReportUpload &&
                        creditReportField &&
                        (() => {
                            const width =
                                validating && !addProgram
                                    ? creditReportField.validateWidth || creditReportField.width
                                    : creditReportField.width;
                            let fieldValidate =
                                validating && !addProgram ? {} : creditReportField.fieldValidate;
                            if (creditReportField.validation) {
                                register(creditReportField.accesKey, creditReportField.validation);
                            }
                            return (
                                <div className="w-full grid grid-cols-8 gap-4 mt-4">
                                    <CreateProgramField
                                        key={creditReportField.title + creditReportField.accesKey}
                                        f={creditReportField}
                                        isEditable
                                        width={width}
                                        fieldValidate={fieldValidate}
                                        person_id={person_id}
                                        lead={lead}
                                        priceError={priceError}
                                        showInfo={showInfo}
                                        validating={validating}
                                        isEditingProgram={isEditingProgram}
                                    />
                                </div>
                            );
                        })()}
                    {/* CLRDC-2234: status line + trigger for the
                        credit-report AI flow. The upload UI itself
                        lives inside the existing `SelectCreditReport`
                        field above; this panel lets the agent decide
                        whether to run the AI extraction at all. */}
                    {showCreditReportUpload && creditReportFile && (
                        <div className="mt-4 flex flex-col gap-2 border border-color3 rounded-[10px] p-3">
                            {!isAnalyzingCreditReport && !creditReportApplied && (
                                <button
                                    type="button"
                                    onClick={handleAnalyzeCreditReport}
                                    className="button-crm self-start !bg-color1 text-color8 text-xs font-medium py-1 px-4"
                                >
                                    Analyze with AI
                                </button>
                            )}
                            {isAnalyzingCreditReport && (
                                <div className="flex items-center gap-2 text-xs text-color1">
                                    <Loader
                                        type="ThreeDots"
                                        color="var(--color-color1)"
                                        height={16}
                                        width={16}
                                    />
                                    <span>
                                        Analyzing credit report…
                                    </span>
                                </div>
                            )}
                            {creditReportError && (
                                <ErrorText msg={creditReportError} />
                            )}
                            {creditReportApplied &&
                                !isAnalyzingCreditReport && (
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-xs text-color1">
                                            AI fields applied. Review the
                                            values below before submitting.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleAnalyzeCreditReport}
                                            className="text-xs text-color1 underline shrink-0"
                                        >
                                            Re-analyze
                                        </button>
                                    </div>
                                )}
                        </div>
                    )}
                    {/* CLRDC-2234: the side viewer can be closed by the
                        agent; this lets them bring it back without
                        re-uploading/re-analyzing anything. */}
                    {showCreditReportUpload &&
                        creditReportViewerUrl &&
                        !isCreditReportViewerVisible && (
                            <button
                                type="button"
                                onClick={handleReopenCreditReportViewer}
                                className="mt-2 flex items-center gap-2 self-start text-xs text-color1 hover:underline"
                            >
                                <EyeIconFilled className="fill-current h-4 w-4" />
                                <span>View Credit Report</span>
                            </button>
                        )}
                    <div className="grid grid-cols-8 gap-4 py-5 text-r-medium-small-2 items-start">
                        {fields?.fields?.map((f: any) => {
                            if (showInfo && isEditingProgram && f.accesKey === "notes_validation") {
                                return <></>;
                            }
                            if (showCreditReportUpload && f.accesKey === "credit_report") {
                                // Already rendered at the top of the popup.
                                return <></>;
                            }

                            const specialPrice =
                                (f.accesKey === "price" || f.accesKey === "cost") &&
                                validating &&
                                !addProgram &&
                                options.special_pricing &&
                                !program.includes("Funding -");
                            const tradelinesValidation =
                                validating && !addProgram && f.accesKey === "tradelines";
                            let isEditable =
                                (showInfo && isEditingProgram
                                    ? !f.automated && f.accesKey !== "name"
                                    : specialPrice ||
                                      (!showInfo &&
                                          (tradelinesValidation ||
                                              (f.editable &&
                                                  (!isFinalValidation ||
                                                      (isFinalValidation &&
                                                          f.finalValidationEditable))))));
                            if (
                                f.accesKey === "estimated_deposit" &&
                                options.special_pricing &&
                                validating &&
                                !addProgram
                            ) {
                                isEditable = true;
                            }
                            if (f.accesKey === "club" && !options.special_pricing && validating && !isEditingProgram) {
                                isEditable = false;
                            }
                            if (
                                f.includeInType &&
                                isClubFico(program) &&
                                !f.includeInType.includes(options.club_type)
                            )
                                return <></>;

                            if (quote) isEditable = false;

                            const width =
                                validating && !addProgram ? f.validateWidth || f.width : f.width;
                            if (f.validation) {
                                register(f.accesKey, f.validation);
                            }
                            let fieldValidate = validating && !addProgram ? {} : f.fieldValidate;

                                return (
                                    <CreateProgramField
                                        key={f.title + f.accesKey}
                                        f={f}
                                        isEditable={isEditable}
                                        width={width}
                                        fieldValidate={fieldValidate}
                                        person_id={person_id}
                                        lead={lead}
                                        priceError={priceError}
                                        showInfo={showInfo}
                                        validating={validating}
                                        isEditingProgram={isEditingProgram}
                                    />
                            );
                        })}
                        <div className="grid md:grid-cols-4 col-span-8 gap-x-2">
                            <DropDownUsers
                                editing={false}
                                title="Telemarketer"
                                name="telemarketer_id_on_creation"
                                assignable
                                value={options?.telemarketer_id_on_creation || ""}
                                titleClassname="font-normal text-r-medium-small-2"
                                className="flex flex-col"
                                inputClassName="outline-none text-color7 border-color3 text-r-medium-small-2 focus:ring-color1 focus:border-color1 transition-all rounded-[7px] py-1 px-2 w-full "
                                showUserImage
                                onChangeFunction={(val) =>
                                    setValue("telemarketer_id_on_creation", val)
                                }
                            >
                                {getErrorType("telemarketer_id_on_creation") === "required" && (
                                    <ErrorText msg={FORM_MSG.REQUIRED} />
                                )}
                            </DropDownUsers>
                            <DropDownUsers
                                editing={false}
                                title="Created by"
                                name="created_by"
                                assignable
                                value={options?.created_by || ""}
                                titleClassname="font-normal text-r-medium-small-2"
                                className="flex flex-col"
                                inputClassName="outline-none text-color7 border-color3 text-r-medium-small-2 focus:ring-color1 focus:border-color1 transition-all rounded-[7px] py-1 px-2 w-full "
                                showUserImage
                                onChangeFunction={(val) => setValue("created_by", val)}
                                // @ts-ignore
                                roles={Object.values(ROLES)}
                            >
                                {getErrorType("created_by") === "required" && (
                                    <ErrorText msg={FORM_MSG.REQUIRED} />
                                )}
                            </DropDownUsers>
                            <DropDownUsers
                                editing={!quote && (!showInfo || isEditingProgram)}
                                title="Sold By"
                                name="seller_id"
                                assignable
                                value={options?.seller_id || ""}
                                titleClassname="font-normal text-r-medium-small-2"
                                className="flex flex-col"
                                inputClassName="outline-none text-color7 border-color3 text-r-medium-small-2 focus:ring-color1 focus:border-color1 transition-all rounded-[7px] py-1 px-2 w-full "
                                showUserImage
                                onChangeFunction={(val) => setValue("seller_id", val)}
                            >
                                {getErrorType("seller_id") === "required" && (
                                    <ErrorText msg={FORM_MSG.REQUIRED} />
                                )}
                            </DropDownUsers>
                            <DropDownUsers
                                editing={!quote && (!showInfo || isEditingProgram)}
                                title="Agent Assigned"
                                name="agent_id"
                                assignable
                                value={options?.agent_id || ""}
                                titleClassname="font-normal text-r-medium-small-2"
                                className="flex flex-col relative"
                                inputClassName="outline-none text-color7 border-color3 text-r-medium-small-2 focus:ring-color1 focus:border-color1 transition-all rounded-[7px] py-1 px-2 w-full "
                                showUserImage
                                onChangeFunction={(val) => setValue("agent_id", val)}
                            >
                                <>
                                    {getErrorType("agent_id") === "required" && (
                                        <ErrorText msg={FORM_MSG.REQUIRED} />
                                    )}
                                    <div className="tooltip absolute right-0 top-0 text-color8 bg-color1 cursor-default text-r-small rounded-full w-4 flex items-center justify-center h-4">
                                        ?
                                        <span className="tooltiptext tooltiptext2">
                                            User who receives the program management commission
                                        </span>
                                    </div>
                                </>
                            </DropDownUsers>
                        </div>
                        {validating && !showInfo && (
                            <div className="w-full flex flex-col col-span-8">
                                <p className="font-bold text-lg">Validation Notes</p>
                                <textarea
                                    className={`outline-none w-full text-color7 border-color3 text-r-medium-small-2 focus:ring-color1
                                    focus:border-none transition-all rounded-[7px] py-1 px-2 mb-0 h-20 ${
                                        addProgram && "bg-color15 text-color1"
                                    }`}
                                    {...register("notes_validation", {})}
                                    disabled={addProgram}
                                />
                            </div>
                        )}
                        {showInfo && (
                            <>
                                <div className="w-full gap-2 grid col-span-8 grid-cols-2">
                                    {quote ? (
                                        <DropdownDynamicList
                                            title={"Reason"}
                                            editing={false}
                                            name={"reason"}
                                            className="flex-col"
                                            outerClass={"col-span-2"}
                                            titleClassName="text-r-medium-small-2"
                                            row={false}
                                            onChangeFunction={(val) =>
                                                setValue("reason" as any, val)
                                            }
                                            placeholder={"Reason"}
                                            search={"Rejected Reason"}
                                            values={(options as any).reason}
                                        />
                                    ) : (
                                        <InputRFH
                                            editing={false}
                                            className="flex-col w-full col-span-2"
                                            titleClassname=""
                                            title="Validated by"
                                            name={"validator.name"}
                                            inputRegister={register}
                                        />
                                    )}
                                </div>
                                {options?.notes_validation && (
                                    <div className="w-full flex flex-col col-span-8">
                                        <p className="font-bold text-lg">Validation Notes</p>
                                        <textarea
                                            className="outline-none w-full text-color7 border-color3 text-r-medium-small-2 rounded-[7px] py-1 px-2 mb-0 h-20 bg-color15 text-color1"
                                            {...register("notes_validation", {})}
                                            disabled
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {options?.status === "CANCELED" && (
                            <div className="w-full grid col-span-8 grid-cols-2">
                                <InputRFH
                                    editing={false}
                                    className="flex-col w-full col-span-1"
                                    titleClassname=""
                                    title="Canceled Reason"
                                    name={"canceled_reason"}
                                    inputRegister={register}
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={`flex flex-row ${
                            backFunction || cancelFunction || onEditQuote || canEditProgram
                                ? "justify-between"
                                : "justify-end"
                        } w-full gap-8`}
                    >
                        {canEditProgram && !isEditingProgram && (
                            <Button
                                title={"Edit Program"}
                                className={"flex self-start text-color8 text-lg py-2 px-10"}
                                clickFunction={handleStartEditing}
                                type="button"
                            />
                        )}
                        {canEditProgram && isEditingProgram && (
                            <div className="flex gap-4">
                                <Button
                                    title={"Cancel"}
                                    className={
                                        "button-crm flex !bg-color11 text-color8 text-lg py-2 px-10"
                                    }
                                    clickFunction={handleCancelEditing}
                                    type="button"
                                />
                                <Button
                                    title={"Save"}
                                    className={"flex self-end text-color8 text-lg py-2 px-10"}
                                    clickFunction={onEditProgram}
                                    isLoading={isUpdating}
                                    type="button"
                                />
                            </div>
                        )}
                        {onEditQuote &&
                            options.status !== "CANCELED" &&
                            ((lead && checkPermission(PERMISSIONS.LEAD_CREATE_QUOTES)) ||
                                (!lead && checkPermission(PERMISSIONS.CLIENT_CREATE_QUOTES))) && (
                                <Button
                                    title={"Update Quote"}
                                    className={"flex self-start text-color8 text-lg py-2 px-10"}
                                    clickFunction={() => {
                                        onEditQuote();
                                    }}
                                    isLoading={isUpdating && options.quote}
                                    type="button"
                                />
                            )}
                        {backFunction && (
                            <button
                                className="button-crm flex text-color8 text-lg py-2 px-10"
                                onClick={(e) => goBack(e)}
                            >
                                {"Back"}
                            </button>
                        )}
                        {cancelFunction && (
                            <Button
                                title={"Cancel"}
                                className={
                                    "button-crm flex !bg-color11 text-color8 text-lg py-2 px-10"
                                }
                                clickFunction={cancelFunction}
                                isLoading={isUpdating}
                                type="button"
                            />
                        )}
                        {onSubmit &&
                            !onEditQuote &&
                            !validating &&
                            ((lead && checkPermission(PERMISSIONS.LEAD_CREATE_QUOTES)) ||
                                (!lead && checkPermission(PERMISSIONS.CLIENT_CREATE_QUOTES))) && (
                                <Button
                                    title={"Save as Quote"}
                                    className={"flex self-end text-color8 text-lg py-2 px-10"}
                                    clickFunction={() => {
                                        setValue("quote", true);
                                        register("payment_type", { required: false });
                                        register("estimated_deposit", { required: false });
                                        register("num_payments", { required: false });
                                        register("next_payment", { required: false });
                                        register("payment_interval", { required: false });
                                        register("payment_method_id", { required: false });
                                        onSubmit();
                                    }}
                                    isLoading={isUpdating && options.quote}
                                    type="button"
                                />
                            )}
                        {onSubmit &&
                            !onlySaveQuote &&
                            !isEditingProgram &&
                            options.status !== "CANCELED" &&
                            ((lead && checkPermission(PERMISSIONS.LEAD_CREATE_PROGRAM)) ||
                                (!lead && checkPermission(PERMISSIONS.CLIENT_CREATE_PROGRAM))) && (
                                <Button
                                    title={"Submit Program"}
                                    className={"flex self-end text-color8 text-lg py-2 px-10"}
                                    clickFunction={onSubmit}
                                    isLoading={isUpdating && !options.quote}
                                    type="button"
                                />
                            )}
                    </div>
                </div>
            </div>
            {/* CLRDC-2234: side viewer for the selected credit-report PDF.
                Renders next to the popup when the agent picked one from
                `SelectCreditReport`. The iframe src is a blob URL built
                from the bytes downloaded via `fetcherGetDownload` in the
                effect above. */}
            {showCreditReportUpload && creditReportViewerUrl && isCreditReportViewerVisible && (
                <div className="hidden md:flex flex-col h-[95vh] min-h-0 w-[55vw] max-w-[900px] bg-color8 rounded-[2rem] shadow-md overflow-hidden relative">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-color3 shrink-0">
                        <div className="flex flex-col">
                            <p className="font-inter font-semibold text-sm">
                                Credit Report Viewer
                            </p>
                            <p className="text-xs text-color6 truncate max-w-[55vw]">
                                {creditReportDocumentName}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="text-xs text-color11 underline"
                            onClick={handleCloseCreditReportViewer}
                        >
                            Close
                        </button>
                    </div>
                    <iframe
                        src={creditReportViewerUrl}
                        title="credit-report-pdf"
                        className="w-full flex-1 min-h-0 bg-white border-0"
                    />
                </div>
            )}
            </div>
        </div>
    );
};
